<!--
  State-gated detail region for `sbi.drilldown(value, body, opts)`.

  Two presentation modes, selected by `node.display`:

  - `"inline"` (default) — renders the body in place, pushing siblings
    below it. Same behaviour the primitive has always had. The
    `whenLabel` footnote always renders, even when the body is empty
    (the gating state was falsy) so the region's purpose is visible.
  - `"modal"` — lifts the body into a native top-layer `<dialog>` with
    X / Esc / backdrop close affordances. The dialog renders nothing
    visible when the gating state is falsy. The `whenLabel` footnote is
    NOT rendered inside the modal — the modal's title and chrome
    already make its purpose clear, and the footnote read as redundant
    UI. Print mode (`PRINT_MODE_KEY` injected by `EmbedHost`) silently
    forces inline rendering so PDFs still contain the body content;
    modals can't render in print.

  Close-affordance dispatch: X / Esc / backdrop emit `chrome-click-target`
  (NOT `click-target`). `EmbedHost.vue` routes `chrome-click-target` to
  `runner.notifyClick(id)` directly — no `rerunning` gate, no
  `data-superseded` shimmer. Closing the modal supersedes whatever
  rerun is in flight (because the close handler calls a state setter,
  which schedules the next pass), which is the desired behaviour.

  Visually mirrors `InsightIf.vue` for inline mode — same banner shape,
  same caption-under-container affordance — so a viewer who recognises
  the branch frame's "Showing when …" footnote reads a drilldown frame
  the same way.

  Accessibility:
    - inline: `role="region"` + `aria-live="polite"` so screen readers
      announce when the panel appears after a click.
    - modal: native `<dialog>` provides `role="dialog"`, `aria-modal`,
      focus trap, and inert background out of the box. The header
      `<h2>` is wired as `aria-labelledby`.

  Embedder theming:
    - inline: data-part="drilldown-container" / "drilldown-error" /
      "drilldown-when-label" — shadow-DOM-safe targeting hooks.
      --lucent-drilldown-bg / --lucent-drilldown-rule /
      --lucent-drilldown-when-label / --lucent-drilldown-error-bg /
      --lucent-drilldown-error-color — supported customisation surface.
      Falls back to the branch tokens so a single theme covers both
      primitives.
    - modal: data-part="drilldown-modal" / "drilldown-modal-header" /
      "drilldown-modal-title" / "drilldown-modal-close" /
      "drilldown-modal-body" plus the --lucent-drilldown-modal-*
      CSS-variable family. Defaults sized to clip to the viewport
      (modal scrolls internally).
-->

<script setup lang="ts">
import { computed, inject, onMounted, ref, useId, watch } from "vue";

import type { ChangeValue } from "../sbi/protocol";
import { PRINT_MODE_KEY } from "../sbi/print-mode";
import type { TreeNode } from "../sbi/validate-render-op";
import InsightNode from "./InsightNode.vue";
import { formatError } from "./errorText.ts";

const props = defineProps<{
  /**
   * The drilldown frame node. `display` selects inline vs modal;
   * `errored` flips the body to the banner; `whenLabel` is the
   * required footnote; `title` / `closeHandlerId` are modal-only.
   */
  node: TreeNode & { kind: "drilldown" };
  /** Pass-through pass-rerunning flag from EmbedHost. */
  loading?: boolean;
  /** Per-pass field → format registry forwarded to children. */
  formats?: Record<string, string>;
}>();

const emit = defineEmits<{
  (e: "click-target", id: string, datum?: Record<string, unknown>, rowIndex?: number): void;
  (e: "change-target", id: string, value: ChangeValue): void;
  /**
   * Chrome-click dispatch path — modal X / Esc / backdrop. EmbedHost
   * routes this to `runner.notifyClick` directly, bypassing the
   * rerun-supersede gate so closing the modal always works.
   */
  (e: "chrome-click-target", id: string): void;
  /** Forwarded from descendant accordions so EmbedHost can surface the DOM event. */
  (e: "accordion-toggle", summary: string, open: boolean): void;
  /** Forwarded from descendant callouts so EmbedHost can surface the DOM event. */
  (e: "callout-rendered", kind: string, title?: string): void;
}>();

// Print-mode override. The PDF print template embeds the insight with
// `print=true`, which provides `PRINT_MODE_KEY` as true; modals can't
// render in PDFs (no user, no Esc, no focus), so we silently fall
// back to inline. The body still renders under the trigger and the
// PDF reader sees the same content they'd see by clicking through in
// the live dashboard.
const isPrint = inject(PRINT_MODE_KEY, () => false);

const effectiveDisplay = computed<"inline" | "modal">(() =>
  isPrint() ? "inline" : props.node.display === "modal" ? "modal" : "inline",
);

// The modal opens iff the post-commit tree shows a non-empty body
// (which corresponds to the gating state being truthy at render
// time). Reading from the rendered tree — rather than from any
// intent-time flag — means a superseded pass that would have opened
// the modal never reaches the `showModal()` call: its render is
// dropped at the host's `passId` gate, the tree isn't swapped, and
// `node.children` stays empty.
const isModalOpen = computed(
  () =>
    effectiveDisplay.value === "modal" &&
    (props.node.children.length > 0 || props.node.errored === true),
);

const dialogRef = ref<HTMLDialogElement | null>(null);

function syncDialogOpenness(open: boolean): void {
  const dlg = dialogRef.value;
  if (!dlg) return;
  if (open && !dlg.open) dlg.showModal();
  if (!open && dlg.open) dlg.close();
}

watch(
  isModalOpen,
  (open) => syncDialogOpenness(open),
  // `flush: "post"` so the watch fires AFTER the DOM has rendered the
  // `<dialog>` element; otherwise `dialogRef.value` is null on the
  // first truthy transition (the v-if mounts the element on the same
  // tick as the watch fires).
  { flush: "post" },
);

// First-paint sync: when the modal is already "open" at mount (e.g.
// PDF round-trip pre-populates author state, or hot-reload mid-drill),
// the `watch` above doesn't fire (no value transition) and the
// `<dialog>` would render in its default closed state. `onMounted`
// runs after the v-if mounts the `<dialog>`, so `dialogRef.value` is
// resolved.
onMounted(() => syncDialogOpenness(isModalOpen.value));

const titleId = useId();

const modalTitle = computed(() => props.node.title ?? "Detail");

const headlineText = computed(() =>
  formatError("Couldn't load this section.", props.node.exId),
);

function emitClose(): void {
  const id = props.node.closeHandlerId;
  if (!id) return;
  emit("chrome-click-target", id);
}

// The dialog's `close` event fires on Esc AND on programmatic
// `.close()` calls (the latter happens when our `isModalOpen` watch
// drives the close on falsy state). Distinguish the two so the watch's
// own programmatic close doesn't re-trigger `emitClose` (which would
// fire the author's `onClose` after the state had already gone
// falsy — harmless but noisy).
function onDialogClose(): void {
  if (isModalOpen.value) {
    // Native close happened while the tree still says "open" — that's
    // a user Esc press. Run the author's close path.
    emitClose();
  }
}

// Backdrop click. The native `<dialog>`'s ::backdrop swallows clicks
// and delivers them as a `click` event with `event.target === dialog`
// (the dialog itself is the closest element); inner clicks have
// `event.target` set to the actual content. Compare references to
// distinguish.
function onDialogClick(event: MouseEvent): void {
  if (event.target === dialogRef.value) {
    emitClose();
  }
}
</script>

<template>
  <div class="drilldown-root">
    <!--
      Single-root wrapper. The v-if/v-else below would otherwise make
      this a multi-root (fragment) component, which @vue/test-utils
      does not intercept emits from cleanly. The wrapping div has
      `display: contents` so the inline / modal layouts render
      exactly as if they were the component root.
    -->
    <!-- Modal presentation -->
    <dialog
      v-if="effectiveDisplay === 'modal'"
      ref="dialogRef"
    class="drilldown-modal"
    data-part="drilldown-modal"
    :aria-labelledby="titleId"
    @close="onDialogClose"
    @click="onDialogClick"
  >
    <header
      class="drilldown-modal-header"
      data-part="drilldown-modal-header"
    >
      <h2
        :id="titleId"
        class="drilldown-modal-title"
        data-part="drilldown-modal-title"
      >
        {{ modalTitle }}
      </h2>
      <button
        type="button"
        class="drilldown-modal-close"
        data-part="drilldown-modal-close"
        aria-label="Close"
        @click="emitClose"
      >
        &times;
      </button>
    </header>

    <div
      class="drilldown-modal-body"
      data-part="drilldown-modal-body"
    >
      <!-- Body threw — render the "couldn't load" banner in place of
           the children. Same shape as the inline error banner so a
           viewer reading the dashboard recognises both. -->
      <div
        v-if="node.errored"
        class="drilldown-error"
        data-part="drilldown-error"
        role="status"
      >
        <p class="drilldown-error-headline">{{ headlineText }}</p>
        <p v-if="node.errorMessage" class="drilldown-error-detail">
          {{ node.errorMessage }}
        </p>
      </div>

      <template v-else>
        <InsightNode
          v-for="(child, i) in node.children"
          :key="i"
          :node="child"
          :loading="loading"
          :formats="formats"
          @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
          @change-target="(id, value) => $emit('change-target', id, value)"
          @chrome-click-target="(id) => $emit('chrome-click-target', id)"
          @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
          @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
        />
      </template>
    </div>
  </dialog>

  <!-- Inline presentation (default + print-mode fallback) -->
  <div
    v-else
    class="drilldown"
    data-part="drilldown-container"
    :data-errored="node.errored ? 'true' : 'false'"
    :data-collapsed="node.children.length === 0 && !node.errored ? 'true' : 'false'"
    role="region"
    aria-live="polite"
  >
    <!--
      Body threw — render a "couldn't load" banner in place of the
      children. The whole-page render continues normally; the wrapper
      also fires a `lucent-error` event with `code:
      "drilldown_body_error"` so embedders can react.
    -->
    <div
      v-if="node.errored"
      class="drilldown-error"
      data-part="drilldown-error"
      role="status"
    >
      <p class="drilldown-error-headline">Couldn't load this section.</p>
      <p v-if="node.errorMessage" class="drilldown-error-detail">
        {{ node.errorMessage }}
      </p>
    </div>

    <!--
      Resolved children. When the body was gated off (the state value
      was falsy) `children` is empty and this loop renders nothing —
      the wrapper collapses to just the footnote, which the
      `data-collapsed` attr surfaces to CSS for any embedder-supplied
      "hide the empty region entirely" rule.
    -->
    <template v-else>
      <InsightNode
        v-for="(child, i) in node.children"
        :key="i"
        :node="child"
        :loading="loading"
        :formats="formats"
        @click-target="(id, datum, rowIndex) => $emit('click-target', id, datum, rowIndex)"
        @change-target="(id, value) => $emit('change-target', id, value)"
        @chrome-click-target="(id) => $emit('chrome-click-target', id)"
        @accordion-toggle="(summary, open) => $emit('accordion-toggle', summary, open)"
        @callout-rendered="(kind, title) => $emit('callout-rendered', kind, title)"
      />
    </template>

    <!--
      The required viewer-facing footnote. Always rendered (even when
      collapsed or errored) so the region's purpose is visible
      regardless of state — empty space and broken space look the same
      without it.
    -->
    <p
      class="drilldown-when-label"
      data-part="drilldown-when-label"
    >
      Showing {{ node.whenLabel }}.
    </p>
  </div>
  </div>
</template>

<style scoped>
/* Root wrapper exists only so the component has a single root (so
   @vue/test-utils can intercept emits and so any future component-
   level `data-*` / `aria-*` attribute lands somewhere sensible).
   `display: contents` makes it invisible to layout — the inline /
   modal child renders as if it were the component root, with no
   added box, flex axis, or margin collapse. */
.drilldown-root {
  display: contents;
}

.drilldown {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: var(--lucent-drilldown-bg, var(--lucent-branch-bg, transparent));
  border-radius: var(--radius, 8px);
}

.drilldown-error {
  padding: 12px 14px;
  border: 1px solid var(--lucent-drilldown-error-rule, var(--lucent-branch-error-rule, var(--rule, #e5e7eb)));
  border-radius: var(--radius, 8px);
  background: var(--lucent-drilldown-error-bg, var(--lucent-branch-error-bg, rgba(176, 0, 32, 0.04)));
  color: var(--lucent-drilldown-error-color, var(--lucent-branch-error-color, var(--lucent-error, #b00020)));
}
.drilldown-error-headline {
  margin: 0;
  font-weight: 600;
  font-size: 14px;
}
.drilldown-error-detail {
  margin: 4px 0 0;
  font-size: 12px;
  opacity: 0.85;
}

.drilldown-when-label {
  margin: 0;
  font-size: 12px;
  color: var(--lucent-drilldown-when-label, var(--lucent-branch-when-label, var(--card-muted, #6b7280)));
  font-style: italic;
}

/* Modal.

   The flex layout is gated on `[open]` because Vue scoped styles add
   a `[data-v-XXX]` attribute selector to `.drilldown-modal`, bumping
   its specificity (0,0,2,0) above the UA's
   `dialog:not([open]) { display: none }` (0,0,1,1). Without this
   gate, the dialog stays visible after `dlg.close()` — making both
   "modal shows up when state is falsy" and "X button does nothing"
   look like component bugs when they're actually a CSS shadowing
   problem. Properties that don't affect visibility (padding, border,
   border-radius, etc.) stay outside the `[open]` selector so they're
   inherited by any future open/closed transition affordance. */
.drilldown-modal {
  padding: 0;
  border: none;
  background: var(--lucent-drilldown-modal-bg, var(--card, white));
  color: inherit;
  border-radius: var(--lucent-drilldown-modal-radius, var(--radius, 8px));
  max-width: var(--lucent-drilldown-modal-max-width, min(90vw, 720px));
  max-height: var(--lucent-drilldown-modal-max-height, min(85vh, 900px));
  width: 100%;
  overflow: hidden;
}
/* `dvh` tracks the mobile browser's collapsing toolbar. Gated with
   @supports rather than a second declaration: a `var()` declaration
   that fails resolves to `none` at computed time instead of falling
   back to the line above. */
@supports (height: 1dvh) {
  .drilldown-modal {
    max-height: var(--lucent-drilldown-modal-max-height, min(85dvh, 900px));
  }
}
/* Phones: full screen. Viewport query on purpose — the dialog is sized
   against the screen, not the embed. Embedder-set size tokens still
   win. */
@media (max-width: 639px) {
  .drilldown-modal {
    max-width: var(--lucent-drilldown-modal-max-width, 100vw);
    max-height: var(--lucent-drilldown-modal-max-height, 100%);
    height: 100%;
    margin: 0;
    border-radius: 0;
  }
  .drilldown-modal-header {
    padding-top: max(14px, env(safe-area-inset-top, 0px));
  }
  .drilldown-modal-body {
    padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
  }
}

.drilldown-modal[open] {
  display: flex;
  flex-direction: column;
}

.drilldown-modal::backdrop {
  background: var(--lucent-drilldown-modal-backdrop, rgba(0, 0, 0, 0.4));
}

.drilldown-modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: var(--lucent-drilldown-modal-header-pad, 14px 16px);
  border-bottom: 1px solid var(--lucent-drilldown-modal-rule, var(--rule, #e5e7eb));
  background: var(--lucent-drilldown-modal-header-bg, transparent);
  color: var(--lucent-drilldown-modal-header-fg, inherit);
}

.drilldown-modal-title {
  margin: 0;
  flex: 1 1 auto;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
  text-align: var(--lucent-drilldown-modal-title-align, left);
  /* Keep long titles from pushing the close button off-screen: wrap to
     two lines (one line truncated too hard on phones), then clamp. */
  min-width: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.drilldown-modal-close {
  flex: 0 0 auto;
  appearance: none;
  background: transparent;
  border: none;
  color: inherit;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  min-width: 36px;
  min-height: 36px;
  display: inline-grid;
  place-items: center;
  border-radius: 4px;
  touch-action: manipulation;
}
.drilldown-modal-close:hover,
.drilldown-modal-close:focus-visible {
  background: var(--lucent-hover-bg, rgba(0, 0, 0, 0.05));
  outline: none;
}
.drilldown-modal-close:focus-visible {
  box-shadow: 0 0 0 2px var(--lucent-focus, currentColor);
}
@media (pointer: coarse) {
  .drilldown-modal-close {
    min-width: 44px;
    min-height: 44px;
  }
}

.drilldown-modal-body {
  /* Internal scroll so a tall body doesn't push the dialog past the
     viewport — `max-height` on the dialog plus this overflow gives us
     a header / scrollable-body / footer layout for free. */
  overflow: auto;
  overscroll-behavior: contain;
  padding: var(--lucent-drilldown-modal-body-pad, 16px);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  /* Container slot: nested components' @container queries measure
     this cell, not the page. Safe because the slot is stretched by
     its parent, never sized to its content. */
  container-type: inline-size;
}

</style>
